/**
 * `${{key}}` placeholder grammar — the only place the token pattern lives.
 * - Keys follow the API's rule: `[A-Za-z0-9_-]`, max 67 chars (random UUIDs in practice).
 */
const TOKEN_SOURCE = String.raw`\$\{\{([A-Za-z0-9_-]{1,67})\}\}`;

/** Fresh global regex per call; a shared `g` regex would leak `lastIndex` between callers. */
export const placeholderTokenPattern = (): RegExp => new RegExp(TOKEN_SOURCE, 'g');

export const buildPlaceholderToken = (key: string): string => `\${{${key}}}`;

/** New random key for a placeholder definition (one per insertion). */
export const createPlaceholderKey = (): string => crypto.randomUUID();

export type PlaceholderTokenSegment = { token: string; key: string };
export type TextSegment = string | PlaceholderTokenSegment;

/** Splits text into literal strings and tokens, in order; repeated tokens are kept. */
export const splitPlaceholders = (text: string): TextSegment[] => {
  const segments: TextSegment[] = [];
  let last = 0;
  for (const match of text.matchAll(placeholderTokenPattern())) {
    if (match.index > last) segments.push(text.slice(last, match.index));
    segments.push({ token: match[0], key: match[1] });
    last = match.index + match[0].length;
  }
  if (last < text.length) segments.push(text.slice(last));
  return segments;
};

/** Unique keys referenced by the text, in first-seen order. */
export const extractPlaceholderKeys = (text: string): string[] => [
  ...new Set(Array.from(text.matchAll(placeholderTokenPattern()), (match) => match[1])),
];
